import { Component, OnInit, OnDestroy } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WalletService, WalletData, UserWallet, WriterProgress, Transaction, WriterEarnings } from '../wallet.service';
import { RewardsService, RechargePlan } from '../rewards.service';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth.service';
import { PromotionService, UserStory, PromotedStory } from '../promotion.service';
import { MswipeService } from '../mswipe.service';
import { lastValueFrom } from 'rxjs';
import { PaymentService } from '../payment.service';

@Component({
  selector: 'app-wallet',
  standalone: true,
  imports: [HeaderComponent, CommonModule, FormsModule],
  templateUrl: './wallet.component.html',
  styleUrl: './wallet.component.css'
})
export class WalletComponent implements OnInit, OnDestroy {
  // Wallet data
  readerWallet: UserWallet = {
    balance: 0,
    coins: 0,
    locked_balance: 0,
    total_earned: 0,
    total_withdrawn: 0
  };

  writerWallet: UserWallet = {
    balance: 0,
    coins: 0,
    locked_balance: 1000,
    total_earned: 0,
    total_withdrawn: 0,
    writer_earnings: 0
  };

  // Writer earnings from user_wallet table
  writerEarnings: WriterEarnings = {
    status: 'success',
    total_earnings: 0,
    available_balance: 0,  
    total_transferred: 0,
    total_episodes_sold: 0,
    writer_earnings: 0
  };

  // Writer progress
  writerProgress: WriterProgress = {
    stories_count: 0,
    episodes_count: 0,
    total_views: 0,
    followers_count: 0,
    is_unlocked: false,
    completed_stories: [],
    total_stories: 0
  };

  // Recharge system
  rechargePlans: RechargePlan[] = [];
  selectedPlan: RechargePlan | null = null;
  
  // Transfer system properties
  transferAmount: number = 0;
  withdrawalAmount: number = 0;
  
  // Transaction history
  readerTransactions: Transaction[] = [];
  writerTransactions: Transaction[] = [];

  // Tabs
  activeTab: 'reader' | 'writer' | 'promotion' = 'reader';
  isLoading: boolean = false;
  paymentProcessing: boolean = false;
  showRechargeModal: boolean = false;
  showPlanSelection: boolean = true;

  // Promotion data
  userStories: UserStory[] = [];
  promotedStories: PromotedStory[] = [];
  selectedPromotionStory: any = null;
  promotionTransferAmount: number = 0;
  isPromotionLoading: boolean = false;
  promotionBalance: number = 0;
  platformFeePercentage: number = 20;

  // New properties for promotion recharge
  showTransferSection: boolean = false;
  showDirectRecharge: boolean = false;
  directRechargeAmount: number = 0;

  // Constants for eligibility
  readonly REQUIRED_STORIES = 3;
  readonly REQUIRED_EPISODES_PER_STORY = 9;
  readonly REQUIRED_VIEWS = 1;
  readonly REQUIRED_FOLLOWERS = 10;

  selectedTransferType: string = '';
  
  private paymentStatusInterval: any;

  constructor(
    private walletService: WalletService,
    private rewardsService: RewardsService,
    private http: HttpClient,
    private authService: AuthService,
    private promotionService: PromotionService,
    private mswipeService: MswipeService,
    private paymentService: PaymentService,
  ) {}

  ngOnInit() {
    this.loadWalletData();
    this.loadRechargePlans();
    this.loadMSwipeScript();
    this.loadPlatformFee();
    this.loadUserStories();
    this.checkMonetizationEligibility();
  }

  // MSwipe script load karein
  loadMSwipeScript() {
    this.rewardsService.loadMSwipeScript().catch(error => {
      
    });
  }

 // Reader wallet recharge
async processRecharge() {
  if (!this.selectedPlan) return;

  this.paymentProcessing = true;

  try {
   

    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not logged in. Please login again.');
    }

    // Step 1: Create transaction record
    const transactionResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/initiate_payment.php', {
        plan_id: this.selectedPlan?.id
      }, { withCredentials: true })
    );

    if (transactionResponse.status !== 'success') {
      throw new Error('Failed to create transaction record');
    }

    const transactionId = transactionResponse.transaction_id;
    const smslink = transactionResponse.smslink;

   

    if (!smslink) {
      throw new Error('Payment link not generated. Please try again.');
    }

    // Step 2: Open payment popup with success callback
    this.openMSwipePopup(smslink, transactionId);
    
  } catch (error: any) {
    this.paymentProcessing = false;
   
    alert('❌ Payment failed: ' + error.message);
  }
}

// Updated popup handling with success detection
private openMSwipePopup(paymentLink: string, transactionId: string) {
  const width = 500;
  const height = 700;
  const left = (screen.width - width) / 2;
  const top = (screen.height - height) / 2;
  

  
  const popup = window.open(
    paymentLink, 
    'MSwipePayment', 
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );
  
  if (!popup) {
    alert('Popup blocked! Please allow popups for this site.');
    this.paymentProcessing = false;
    return;
  }
  
  // ✅ NEW: Listen for payment completion message from popup
  const messageHandler = (event: MessageEvent) => {
 
    
    if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
      // Payment successful from popup

      window.removeEventListener('message', messageHandler);
      this.handlePaymentSuccess(transactionId);
    }
    
    if (event.data && event.data.type === 'PAYMENT_FAILED') {
      // Payment failed from popup

      window.removeEventListener('message', messageHandler);
      this.handlePaymentFailed(transactionId);
    }
  };
  
  window.addEventListener('message', messageHandler);
  
  // ✅ NEW: Check if popup is redirected to success URL
  const popupCheck = setInterval(() => {
    try {
      if (popup.closed) {
        clearInterval(popupCheck);
        window.removeEventListener('message', messageHandler);
        this.paymentProcessing = false;
        
        // If popup closed without success message, check payment status
        this.checkPaymentStatusFinal(transactionId);
        return;
      }
      
      // Check if popup URL contains success indicator
      const popupUrl = popup.location.href;
      if (popupUrl.includes('payment-success') || popupUrl.includes('success')) {
        clearInterval(popupCheck);
        window.removeEventListener('message', messageHandler);
        popup.close();
        this.handlePaymentSuccess(transactionId);
      }
      
    } catch (error) {
      // Cross-origin error, ignore - we'll use message communication
    }
  }, 2000);
  
  // Timeout after 30 minutes
  setTimeout(() => {
    clearInterval(popupCheck);
    window.removeEventListener('message', messageHandler);
    if (!popup.closed) {
      popup.close();
    }
    this.paymentProcessing = false;
  }, 30 * 60 * 1000);
}

// ✅ NEW: Handle payment success
private async handlePaymentSuccess(transactionId: string) {
  this.paymentProcessing = false;
  
  try {
    // Wait a bit for payment processing
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const verifyResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/verify_payment.php', {
        transaction_id: transactionId
      }, { withCredentials: true })
    );

    if (verifyResponse.status === 'success') {
      alert(`🎉 Payment successful! ${verifyResponse.coins_added} coins added to your wallet.`);
      this.loadWalletData();
      this.closeRechargeModal();
    } else {
      alert('❌ Payment verification failed. Please check your transaction history.');
    }
  } catch (error) {
    alert('Payment completed but verification failed. Please check your transaction history.');
  }
}

// ✅ NEW: Handle payment failed
private handlePaymentFailed(transactionId: string) {
  this.paymentProcessing = false;
  alert('❌ Payment was cancelled or failed. No coins were added.');
}

// ✅ NEW: Final payment status check (when popup closes without clear success)
private async checkPaymentStatusFinal(transactionId: string) {
  try {
    const verifyResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/verify_payment.php', {
        transaction_id: transactionId
      }, { withCredentials: true })
    );

    if (verifyResponse.status === 'success') {
      alert(`🎉 Payment successful! ${verifyResponse.coins_added} coins added to your wallet.`);
      this.loadWalletData();
      this.closeRechargeModal();
    } else {
      alert('❌ Payment was not completed. Please try again.');
    }
  } catch (error) {
    alert('Unable to verify payment status. Please check your transaction history.');
  }
}

  private async createTransactionRecord(): Promise<any> {
    // Create transaction record in your database
    const transactionResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/initiate_payment.php', {
        plan_id: this.selectedPlan?.id
      }, { withCredentials: true })
    );

    if (transactionResponse.status !== 'success') {
      throw new Error('Failed to create transaction record');
    }

    return transactionResponse;
  }

 

private async checkPaymentStatus(transactionId: string) {
  try {
    // Wait longer for payment processing
    await new Promise(resolve => setTimeout(resolve, 8000));
    
    const verifyResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/verify_payment.php', {
        transaction_id: transactionId
      }, { withCredentials: true })
    );
    if (verifyResponse.status === 'success') {
      alert(`🎉 Payment successful! ${verifyResponse.coins_added} coins added to your wallet.`);
      this.loadWalletData();
      this.closeRechargeModal();
    } else {
      alert('❌ Payment failed or was not completed. Please try again.');
    }
  } catch (error) {
    alert('Unable to verify payment status. Please check your transaction history.');
  }
}

  // NEW: Simplified promotion recharge method using PaymentService
async rechargePromotionDirect() {
  if (this.directRechargeAmount < 10) {
    alert('Minimum recharge amount is ₹10');
    return;
  }

  this.isPromotionLoading = true;

  try {
    // Use the updated payment service method
    const result = await this.paymentService.initiatePromotionRecharge(this.directRechargeAmount);
    
    if (result.status === 'success') {
      this.showDirectRecharge = false;
      this.directRechargeAmount = 0;
      
    } else {
      // Agar status 'success' nahi hai tabhi error throw karo
      throw new Error(result.message || 'Promotion recharge failed');
    }

  } catch (error: any) {
    // Specific error messages based on error type
    if (error.message.includes('Payment initiated')) {
      // Yeh actually success case hai, error mat dikhao
    } else {
      alert('❌ Promotion recharge failed: ' + error.message);
    }
  } finally {
    this.isPromotionLoading = false;
  }
}

  loadWalletData() {
    this.isLoading = true;
    
    this.walletService.getWalletData('all').subscribe({
      next: (data: WalletData) => {
        if (data.reader_wallet) {
          this.readerWallet = { ...data.reader_wallet };
          this.readerTransactions = data.reader_transactions || [];
        }
        
        if (data.writer_wallet) {
          this.writerWallet = data.writer_wallet;
          this.writerTransactions = data.writer_transactions || [];
        }
          
        if (data.writer_progress) {
          this.writerProgress = data.writer_progress;
        }
        
        this.isLoading = false;
        this.loadWriterEarnings();
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  checkMonetizationEligibility() {
    const userId = this.authService.getCurrentUserId();
    this.http.get(`https://stories.funtelstudio.com/storyapi/check_monetization_eligibility.php?user_id=${userId}`, {
        withCredentials: true
    }).subscribe({
        next: (response: any) => {
            if (response.status === 'success') {
                if (response.monetization_updated) {
                    this.loadWalletData();
                    this.loadWriterEarnings();
                    
                    if (response.is_monetized) {
                        setTimeout(() => {
                            alert('🎉 Congratulations! Your channel is now monetized. ₹1000 bonus has been added to your wallet.');
                        }, 1000);
                    }
                }
            }
        },
        error: (error) => {
            console.error('Monetization check failed:', error);
        }
    });
  }

  // Load writer earnings from user_wallet table
  loadWriterEarnings() {
    this.walletService.getWriterEarnings().subscribe({
      next: (response: WriterEarnings) => {
        if (response.status === 'success') {
          this.writerEarnings = response;
          this.writerWallet.balance = response.writer_earnings || response.total_earnings;
          this.writerWallet.writer_earnings = response.writer_earnings || response.total_earnings;
        }
      },
      error: (error) => {
        console.error('Error loading writer earnings:', error);
      }
    });
  }

  // Load recharge plans
  loadRechargePlans() {
    this.rewardsService.getRechargePlans().subscribe({
      next: (response) => {
        this.rechargePlans = response.plans;
        if (this.rechargePlans.length > 0) {
          this.selectedPlan = this.rechargePlans[0];
        }
      },
      error: (error) => {
        console.error('Error loading recharge plans:', error);
      }
    });
  }

  // Switch tabs
  switchTab(tab: 'reader' | 'writer' | 'promotion') {
    this.activeTab = tab;
    if (tab === 'writer') {
      this.loadWriterEarnings();
    } else if (tab === 'promotion') {
      this.loadPromotedStories();
      this.loadPromotionBalance();
      this.loadUserStories();
    }
  }

  // Load platform fee
  loadPlatformFee() {
    this.http.get<any>('https://stories.funtelstudio.com/storyapi/get_platform_fee.php').subscribe({
      next: (response) => {
        this.platformFeePercentage = parseFloat(response.platform_fee_percentage) || 20;
      },
      error: () => {
        this.platformFeePercentage = 20;
      }
    });
  }

  // Platform fee calculate karo
  calculatePlatformFee(amount: number): number {
    return (amount * this.platformFeePercentage) / 100;
  }

  // Net withdrawal amount calculate karo
  calculateNetWithdrawal(amount: number): number {
    const fee = this.calculatePlatformFee(amount);
    return amount - fee;
  }

  // Format currency
  formatCurrency(amount: number): string {
    return '₹' + amount.toFixed(2);
  }

  // TRANSFER TO READER WALLET
  nTransferTypeChange() {
    this.transferAmount = 0;
    this.selectedPromotionStory = null;
    
    if (this.selectedTransferType === 'promotion') {
      this.loadUserStories();
    }
  }
  
  // Validate transfer
  isTransferValid(): boolean {
    if (!this.selectedTransferType || this.transferAmount <= 0) {
      return false;
    }
    
    if (this.transferAmount > this.writerWallet.balance) {
      return false;
    }
    
    return true;
  }
  
  // Process transfer based on selection
  processTransfer() {
    if (!this.isTransferValid()) {
      return;
    }
    
    switch(this.selectedTransferType) {
      case 'reader':
        this.transferToReader();
        break;
      case 'promotion':
        this.transferToPromotion();
        break;
      default:
        alert('Please select a valid transfer type');
    }
  }
  
  // Transfer to Reader Wallet
  transferToReader() {
    if (this.transferAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (this.transferAmount > this.writerWallet.balance) {
      alert(`Insufficient available balance! Available: ₹${this.writerWallet.balance}`);
      return;
    }

    this.isLoading = true;

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/transfer_wallet_funds.php', {
      user_id: this.authService.getCurrentUserId(),
      amount: this.transferAmount,
      transfer_type: 'reader'
    }, {
      withCredentials: true
    }).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          this.loadWalletData();
          this.loadWriterEarnings();
          this.transferAmount = 0;
          this.selectedTransferType = '';
          alert(`✅ ₹${response.amount_transferred} transferred to Reader Wallet successfully!`);
        } else {
          alert('Transfer failed: ' + response.message);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Transfer to reader error:', error);
        this.isLoading = false;
        alert('Transfer failed. Please try again.');
      }
    });
  }

  // TRANSFER TO PROMOTION WALLET
  transferToPromotion() {
    if (this.transferAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (this.transferAmount > this.writerWallet.balance) {
      alert(`Insufficient available balance! Available: ₹${this.writerWallet.balance}`);
      return;
    }

    console.log('🔄 Transferring to Promotion Wallet:', this.transferAmount);

    this.isLoading = true;

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/transfer_wallet_funds.php', {
      user_id: this.authService.getCurrentUserId(),
      amount: this.transferAmount,
      transfer_type: 'promotion'
    }, {
      withCredentials: true
    }).subscribe({
      next: (response: any) => {
        console.log('✅ Transfer to Promotion Response:', response);
        
        if (response && response.status === 'success') {
          this.loadWalletData();
          this.loadWriterEarnings();
          this.loadPromotionBalance();
          
          this.transferAmount = 0;
          this.selectedTransferType = '';
          
          alert(`✅ ₹${response.amount_transferred} added to Promotion Balance successfully!\nNew Promotion Balance: ₹${response.new_promotion_balance || 0}`);
        } else {
          console.error('Invalid response format:', response);
          alert('Transfer failed: Invalid response from server');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Transfer to promotion error:', error);
        this.isLoading = false;
        
        if (error.error && error.error.message) {
          alert('Transfer failed: ' + error.error.message);
        } else {
          alert('Transfer failed. Please try again.');
        }
      }
    });
  }

  onTransferTypeChange() {
    this.transferAmount = 0;
  }

  // WITHDRAW FUNDS
  withdrawFunds() {
    if (!this.writerProgress.is_unlocked) {
      alert('Please complete all eligibility criteria first.');
      return;
    }

    const withdrawAmount = this.writerWallet.balance;
    
    if (withdrawAmount <= 0) {
      alert('No funds available for withdrawal');
      return;
    }

    this.isLoading = true;

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/transfer_wallet_funds.php', {
      user_id: this.authService.getCurrentUserId(),
      amount: withdrawAmount,
      transfer_type: 'withdrawal'
    }, {
      withCredentials: true
    }).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          this.writerWallet.balance = 0;
          this.writerWallet.writer_earnings = 0;
          this.writerWallet.total_withdrawn += this.calculateNetWithdrawal(withdrawAmount);
          
          alert(`Withdrawal request submitted successfully! You will receive ₹${this.calculateNetWithdrawal(withdrawAmount)} after platform fee.`);
          
          setTimeout(() => {
            this.loadWalletData();
            this.loadWriterEarnings();
          }, 1000);
        } else {
          alert('Withdrawal failed: ' + response.message);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error withdrawing funds:', error);
        this.isLoading = false;
        alert('Withdrawal failed. Please try again.');
      }
    });
  }

  // Promotion related methods
  loadUserStories() {
    const userId = this.authService.getCurrentUserId();
    
    if (!userId) {
      console.error('User not logged in');
      return;
    }

    this.promotionService.getPublishedStories(userId.toString()).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.userStories = response.data;
          console.log('📚 Published stories for promotion:', this.userStories);
        } else {
          console.error('Error loading published stories:', response);
          this.userStories = [];
        }
      },
      error: (error) => {
        console.error('Error loading published stories:', error);
        this.userStories = [];
      }
    });
  }

  loadPromotedStories() {
    this.promotionService.getPromotedStories().subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.promotedStories = response.promoted_stories;
          console.log('📊 Promoted Stories Loaded:', this.promotedStories);
        }
      },
      error: (error) => {
        console.error('Error loading promoted stories:', error);
      }
    });
  }

  promoteStory() {
    if (!this.selectedPromotionStory) {
      alert('Please select a story to promote');
      return;
    }

    if (this.promotionBalance <= 0) {
      alert('Please add funds to your promotion balance first');
      return;
    }

    const existingPromotion = this.promotedStories.find(
      p => p.story_id === this.selectedPromotionStory.id && 
      this.getPromotionStatus(p) === 'active'
    );

    if (existingPromotion) {
      if (existingPromotion.status === 'paused') {
        this.updatePromotionStatus(existingPromotion.id, 'active');
      }
      alert('Story is already being promoted!');
      return;
    }

    this.isPromotionLoading = true;

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/promotion_handler.php?action=promote_story_with_balance', {
      story_id: this.selectedPromotionStory.id
    }, {
      withCredentials: true
    }).subscribe({
      next: (response: any) => {
        this.isPromotionLoading = false;
        
        if (response.status === 'success') {
          alert('🎉 Story promotion started! Clicks will be charged from your main promotion balance.');
          this.loadPromotedStories();
          this.selectedPromotionStory = null;
        } else {
          alert('Promotion failed: ' + response.message);
        }
      },
      error: (error) => {
        this.isPromotionLoading = false;
        alert('Promotion failed: ' + error.error?.message || error.message);
      }
    });
  }

  getPromotionStatus(promotion: any): string {
    if (promotion.status === 'paused') {
      return 'paused';
    }
    
    if (this.promotionBalance < 1.00) {
      return 'insufficient_balance';
    }
    
    return promotion.status || 'active';
  }

  updatePromotionStatus(promotionId: number, status: string) {
    this.http.post('https://stories.funtelstudio.com/storyapi/promotion_handler.php?action=update_promotion_status', 
      {
        promotion_id: promotionId,
        status: status
      }, 
      { withCredentials: true }
    ).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          const promotion = this.promotedStories.find(p => p.id === promotionId);
          if (promotion) {
            promotion.status = status;
          }
        }
      },
      error: (error) => {
        alert('Failed to update promotion status');
      }
    });
  }

  loadPromotionBalance() {
    this.http.get<any>('https://stories.funtelstudio.com/storyapi/promotion_handler.php?action=get_promotion_balance', {
      withCredentials: true
    }).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          this.promotionBalance = parseFloat(response.promotion_balance) || 0;
          console.log('💰 Promotion Balance Loaded:', this.promotionBalance);
        } else {
          console.error('Error loading promotion balance:', response.message);
          this.promotionBalance = 0;
        }
      },
      error: (error) => {
        console.error('Error loading promotion balance:', error);
        this.promotionBalance = 0;
      }
    });
  }

  // Helper methods for user data
  getUserData(): { userId: number; email: string; mobile: string; amount: number } {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not logged in');
    }
    
    return {
      userId: userId,
      email: this.getUserEmail(),
      mobile: this.getUserMobile(),
      amount: this.selectedPlan?.amount || this.directRechargeAmount || 0
    };
  }

  getPaymentAmount(transactionId: string): number {
    if (this.directRechargeAmount > 0) {
      return this.directRechargeAmount;
    }
    if (this.selectedPlan) {
      return this.selectedPlan.amount;
    }
    return 0;
  }

  getUserEmail(): string {
    const currentUser = this.authService.getCurrentUserValue();
    return currentUser?.email || 'rahulbonde310@gmail.com';
  }

  getUserMobile(): string {
    const currentUser = this.authService.getCurrentUserValue();
    return currentUser?.mobile || '7977293731';
  }

  // Recharge modal methods
  selectPlan(plan: RechargePlan) {
    this.selectedPlan = plan;
    this.showPlanSelection = false;
  }

  backToPlanSelection() {
    this.selectedPlan = null;
    this.showPlanSelection = true;
  }

  openRechargeModal() {
    this.showRechargeModal = true;
    this.showPlanSelection = true; 
    this.selectedPlan = null;
  }

  closeRechargeModal() {
    this.showRechargeModal = false;
    this.showPlanSelection = true;
    this.selectedPlan = null;
    this.paymentProcessing = false;
  }

  // Progress calculation methods
  calculatePromotionProgress(spent: number, budget: number): number {
    return budget > 0 ? (spent / budget) * 100 : 0;
  }

  calculateProgress(current: number, target: number): number {
    return Math.min((current / target) * 100, 100);
  }

  isConditionMet(current: number, target: number): boolean {
    return current >= target;
  }

  get completedStoriesCount(): number {
    return this.writerProgress.stories_count;
  }

  getStoriesProgressMessage(): string {
    const completed = this.completedStoriesCount;
    const remaining = this.REQUIRED_STORIES - completed;
    
    if (completed >= this.REQUIRED_STORIES) {
      return 'Congratulations! You have completed the required 3 stories.';
    } else {
      return `Complete ${remaining} more story(s) with 3 episodes each.`;
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
  }

  getProgressColor(progress: number): string {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-blue-500';
  }

  // Clean up on component destroy
  ngOnDestroy() {
    // this.stopPaymentStatusPolling();
  }
}